import React, { useState, useMemo } from 'react';
import { ArrowLeft, Trash2, Search, Filter, ShieldAlert, CheckCircle2, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useGuru } from '../../../context/GuruContext';
import { AVAILABLE_MENUS, UserPermission } from '../../../types';

export const PortalGuruAnalisisAkses: React.FC = () => {
    const navigate = useNavigate();
    const { userPermissions, deleteUserPermission } = useUserAccess();
    const { guruRecords } = useGuru();

    const [activeTab, setActiveTab] = useState<'duplikat' | 'analisis'>('duplikat');
    const [selectedMenu, setSelectedMenu] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDeletes, setSelectedDeletes] = useState<string[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);

    // --- LOGIKA DUPLIKAT ---
    const duplicates = useMemo(() => {
        const emailMap: Record<string, UserPermission[]> = {};
        userPermissions.forEach(perm => {
            const email = (perm.email || '').toLowerCase().trim();
            if (!email) return;
            if (!emailMap[email]) emailMap[email] = [];
            emailMap[email].push(perm);
        });

        const duplicateEntries: { email: string; records: UserPermission[]; namaGuru: string }[] = [];
        for (const [email, records] of Object.entries(emailMap)) {
            if (records.length > 1) {
                const guru = guruRecords.find(g => g.email?.toLowerCase().trim() === email);
                duplicateEntries.push({
                    email,
                    records,
                    namaGuru: guru ? guru.nama : 'Tidak diketahui (Bukan Guru Aktif)'
                });
            }
        }
        return duplicateEntries;
    }, [userPermissions, guruRecords]);

    const handleToggleSelect = (id: string) => {
        setSelectedDeletes(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        if (selectedDeletes.length === 0) return;
        if (window.confirm(`Apakah Anda yakin ingin menghapus ${selectedDeletes.length} dokumen hak akses yang dipilih?`)) {
            setIsDeleting(true);
            try {
                // Use Promise.all to delete multiple records concurrently
                await Promise.all(selectedDeletes.map(id => deleteUserPermission(id)));
                setSelectedDeletes([]);
                alert('Dokumen terpilih berhasil dihapus.');
            } catch (error) {
                console.error("Gagal menghapus beberapa dokumen", error);
                alert('Gagal menghapus beberapa dokumen.');
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Apakah Anda yakin ingin menghapus dokumen hak akses ini?')) {
            try {
                await deleteUserPermission(id);
                alert('Dokumen berhasil dihapus.');
            } catch (error) {
                console.error("Gagal menghapus", error);
                alert('Gagal menghapus dokumen.');
            }
        }
    };

    // --- LOGIKA ANALISIS MENU ---
    const usersWithSelectedMenu = useMemo(() => {
        if (!selectedMenu) return [];

        let filtered = userPermissions.filter(perm =>
            perm.allowedMenus && perm.allowedMenus.includes(selectedMenu)
        );

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(perm => {
                const guru = guruRecords.find(g => g.email?.toLowerCase().trim() === perm.email?.toLowerCase().trim());
                return perm.email?.toLowerCase().includes(query) || (guru && guru.nama.toLowerCase().includes(query));
            });
        }

        return filtered.map(perm => {
            const guru = guruRecords.find(g => g.email?.toLowerCase().trim() === perm.email?.toLowerCase().trim());
            return {
                ...perm,
                namaGuru: guru ? guru.nama : 'Tidak diketahui'
            };
        }).sort((a, b) => a.namaGuru.localeCompare(b.namaGuru));

    }, [selectedMenu, userPermissions, guruRecords, searchQuery]);

    const getMenuName = (id: string) => {
        return AVAILABLE_MENUS.find(m => m.id === id)?.label || id;
    };

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/portal-guru/admin/pengaturan')}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Analisis Hak Akses</h1>
                            <p className="text-xs text-gray-500 font-medium">Pengaturan Sistem</p>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-t px-4">
                    <button
                        onClick={() => setActiveTab('duplikat')}
                        className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${
                            activeTab === 'duplikat' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Cari Duplikat
                    </button>
                    <button
                        onClick={() => setActiveTab('analisis')}
                        className={`flex-1 py-3 text-sm font-bold border-b-2 transition-colors ${
                            activeTab === 'analisis' ? 'border-brand-primary text-brand-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                    >
                        Analisis Menu
                    </button>
                </div>
            </div>

            <div className="p-4 max-w-4xl mx-auto mt-2">

                {/* TAB: DUPLIKAT */}
                {activeTab === 'duplikat' && (
                    <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3 items-start">
                            <ShieldAlert className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-sm font-bold text-blue-900 mb-1">Pendeteksi Dokumen Ganda</h3>
                                <p className="text-xs text-blue-800 leading-relaxed">
                                    Fitur ini mendeteksi email yang memiliki lebih dari satu dokumen hak akses.
                                    Biasanya ini terjadi karena masalah sinkronisasi versi lama. Hapus dokumen yang jumlah menunya kosong atau lebih sedikit.
                                </p>
                            </div>
                        </div>


                        {selectedDeletes.length > 0 && (
                            <div className="bg-white p-3 rounded-xl border border-rose-200 shadow-sm flex items-center justify-between sticky top-20 z-40">
                                <span className="text-sm font-bold text-gray-700">
                                    <span className="text-rose-600">{selectedDeletes.length}</span> dokumen dipilih
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setSelectedDeletes([])}
                                        disabled={isDeleting}
                                        className="px-3 py-1.5 text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                                    >
                                        Batal
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        disabled={isDeleting}
                                        className="px-3 py-1.5 text-xs font-bold text-white bg-rose-500 hover:bg-rose-600 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50 shadow-sm shadow-rose-200"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        {isDeleting ? 'Menghapus...' : 'Hapus Terpilih'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {duplicates.length === 0 ? (
                            <div className="bg-white p-8 rounded-xl border border-gray-100 text-center flex flex-col items-center justify-center">
                                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center mb-3">
                                    <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                                </div>
                                <h3 className="font-bold text-gray-900">Sistem Bersih</h3>
                                <p className="text-sm text-gray-500 mt-1">Tidak ditemukan dokumen hak akses ganda.</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {duplicates.map((dup, i) => (
                                    <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                                            <div>
                                                <h3 className="font-bold text-gray-900 text-sm">{dup.namaGuru}</h3>
                                                <p className="text-xs text-gray-500">{dup.email}</p>
                                            </div>
                                            <span className="bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-1 rounded-md">
                                                {dup.records.length} Dokumen
                                            </span>
                                        </div>
                                        <div className="p-4 space-y-3">
                                            {dup.records.map((record, j) => (
                                                <div key={record.id} className={`flex flex-col sm:flex-row gap-3 p-3 border rounded-lg transition-colors ${selectedDeletes.includes(record.id) ? 'border-rose-300 bg-rose-50/30' : 'border-gray-100 bg-gray-50/50'}`}>
                                                    <div className="pt-1">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedDeletes.includes(record.id)}
                                                            onChange={() => handleToggleSelect(record.id)}
                                                            className="w-4 h-4 text-rose-500 border-gray-300 rounded focus:ring-rose-500 cursor-pointer"
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex justify-between items-start mb-2">
                                                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Dokumen {j+1}</span>
                                                            <span className="text-[10px] text-gray-400 font-mono bg-white px-1.5 py-0.5 rounded border">ID: {record.id.substring(0, 6)}...</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {record.allowedMenus && record.allowedMenus.length > 0 ? (
                                                                record.allowedMenus.map(m => (
                                                                    <span key={m} className="px-2 py-1 bg-white border border-gray-200 text-gray-600 text-[10px] rounded-md">
                                                                        {getMenuName(m)}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-xs text-rose-500 font-medium flex items-center gap-1 bg-rose-50 px-2 py-1 rounded-md">
                                                                    <AlertCircle className="w-3 h-3" /> Tidak ada menu (Kosong)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex sm:flex-col justify-end items-end gap-2 border-t sm:border-t-0 sm:border-l border-gray-200 pt-3 sm:pt-0 sm:pl-4 mt-2 sm:mt-0">
                                                        <button
                                                            onClick={() => handleDelete(record.id)}
                                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors w-full sm:w-auto justify-center"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            <span>Hapus</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* TAB: ANALISIS MENU */}
                {activeTab === 'analisis' && (
                    <div className="space-y-4">
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                                    Pilih Menu
                                </label>
                                <select
                                    value={selectedMenu}
                                    onChange={(e) => setSelectedMenu(e.target.value)}
                                    className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all appearance-none"
                                >
                                    <option value="">-- Pilih Menu untuk Dianalisis --</option>
                                    {AVAILABLE_MENUS.map(m => (
                                        <option key={m.id} value={m.id}>{m.label} ({m.category})</option>
                                    ))}
                                </select>
                            </div>

                            {selectedMenu && (
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Cari nama atau email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all"
                                    />
                                </div>
                            )}
                        </div>

                        {selectedMenu && (
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                                <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
                                    <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                                        <Filter className="w-4 h-4 text-brand-primary" />
                                        Hasil Analisis
                                    </h3>
                                    <span className="bg-brand-primary/10 text-brand-primary text-xs font-bold px-2.5 py-1 rounded-lg">
                                        {usersWithSelectedMenu.length} Akun
                                    </span>
                                </div>

                                {usersWithSelectedMenu.length === 0 ? (
                                    <div className="p-8 text-center text-gray-500 text-sm">
                                        Tidak ada akun yang memiliki akses ke menu ini.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {usersWithSelectedMenu.map(perm => (
                                            <div key={perm.id} className="p-4 flex justify-between items-center hover:bg-gray-50/50 transition-colors">
                                                <div>
                                                    <p className="font-bold text-gray-900 text-sm">{perm.namaGuru}</p>
                                                    <p className="text-xs text-gray-500">{perm.email}</p>
                                                </div>
                                                <div className="text-right">
                                                    {perm.role && (
                                                        <span className="inline-block px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] font-bold rounded">
                                                            {perm.role.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
